/**
 * What changes INSIDE a month, as something a person can open (UI4).
 *
 * WHAT THIS REPLACES. R8 put the transitions on screen as a `→` glyph with the
 * wording in the cell's `aria-label` and `title`. That reaches a screen reader
 * and a mouse, and nobody else: a tooltip has no keyboard and no touch, so on a
 * phone or on a keyboard the arrow was a mark with no way to read it. The glyph
 * stays exactly where it was and becomes the button it was already pretending
 * to be.
 *
 * ADDITIVE, LIKE THE PAYLOAD IT DRAWS. The cell keeps its own state, its own
 * colour, its own word and its own accessible name — this is one more way to
 * reach the same `cell.transitions`, never a second opinion about them. No
 * transition is derived, re-ordered, de-duplicated or explained here:
 * `normaliseTransitions` already validated and sorted the list, and this file
 * renders it in the order it was handed.
 *
 * EVERY WORD COMES FROM THE SHARED TRUTH. The heading is `TRANSITION_HINT`, the
 * state names are `LIFECYCLE_LABELS`, the arrow is `TRANSITION_MARKER` and the
 * dates go through `formatTransitionDate`, which reads the ISO string's own
 * digits and never builds a `Date` — so no timezone can move a day by one.
 * Inventing wording here is how the two calendars start describing one payload
 * in two languages.
 *
 * THE CONTENT IS PORTALLED, and that is the reason to use the shared primitive
 * rather than a panel of our own: the matrix lives inside `overflow-x-auto`,
 * which would clip anything drawn in place. The popover renders at the document
 * root and escapes that box.
 */
import React from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import {
  LIFECYCLE_LABELS,
  TRANSITION_HINT,
  TRANSITION_MARKER,
  formatTransitionDate,
} from "../../../utils/lifecycleCalendarTruth";

/**
 * The trigger's own size, as the matrix has always drawn it: a 15px glyph
 * tucked beside a state word inside a 60px cell. That is a MOUSE target, and
 * it is the right one there — the cell has no room for more.
 */
const DESKTOP_TRIGGER =
  "ml-1 inline-flex h-[15px] min-w-[15px] shrink-0 items-center justify-center rounded-[3px] font-normal leading-none opacity-80 transition-colors hover:bg-foreground/10 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export default function CalendarTransitionPopover({
  transitions,
  roomName,
  monthLabel,
  // The PHONE passes a bigger target (UI5). A finger is not a cursor, and 15px
  // is roughly a third of the 44-48px the platform guidelines ask for — on the
  // one device R8's tooltip could never serve at all. The default is the
  // desktop string above, unchanged byte for byte, so the matrix keeps the
  // trigger it shipped with.
  triggerClassName = DESKTOP_TRIGGER,
}) {
  const list = transitions || [];
  // NO TRIGGER FOR A MONTH WHERE NOTHING HAPPENS. Not a disabled button and not
  // a dimmed arrow: a control that opens an empty box is worse than no control,
  // and a decorative arrow is what R8 already decided against.
  if (list.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-transition-trigger={list.length}
          aria-label={`${list.length} ${TRANSITION_HINT} anzeigen — ${roomName}, ${monthLabel}`}
          className={triggerClassName}
        >
          {TRANSITION_MARKER}
        </button>
      </PopoverTrigger>

      <PopoverContent align="center" className="w-60 p-3" data-transition-popover>
        <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {TRANSITION_HINT}
        </p>
        <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
          {roomName} · {monthLabel}
        </p>

        <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
          {list.map((tr) => (
            <li
              key={`${tr.date}|${tr.fromState}|${tr.toState}`}
              data-transition-item={tr.date}
              className="text-[12px] leading-tight text-popover-foreground"
            >
              <span
                data-transition-date
                className="block font-semibold tabular-nums"
              >
                {formatTransitionDate(tr.date)}
              </span>
              <span className="block text-muted-foreground">
                <span data-transition-from>{LIFECYCLE_LABELS[tr.fromState]}</span>
                {` ${TRANSITION_MARKER} `}
                <span data-transition-to>{LIFECYCLE_LABELS[tr.toState]}</span>
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
